import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import InfoIcon from "@material-ui/icons/Info";
import DropReasonAutoComplete from "../../../components/AutoComplete/DropReasonAutoComplete";
import { getNaccasDropReasons } from "../../../api/reports/stateBoard/stateBoardReportApi";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: {
      marginBottom: theme.spacing(4),
      padding: theme.spacing(3),
      backgroundColor: "#ffffff",
      borderRadius: theme.shape.borderRadius,
    },
    header: {
      marginBottom: theme.spacing(2),
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    formControl: {
      width: "100%",
      marginBottom: theme.spacing(2),
    },
    tooltipIcon: {
      marginLeft: theme.spacing(1),
      fontSize: "1.2rem",
      color: theme.palette.primary.main,
    },
    reasonHeader: {
      display: "flex",
      alignItems: "center",
      marginBottom: theme.spacing(1),
    },
    reasonSection: {
      backgroundColor: theme.palette.grey[50],
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    reasonCode: {
      backgroundColor: theme.palette.grey[200],
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.shape.borderRadius,
      marginLeft: theme.spacing(1),
      fontSize: "0.8rem",
      fontWeight: 700,
    },
    exemptReason: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
    },
  })
);

type DropMapping = {
  naccasReasonId: string;
  advantageReasons: string[];
};

interface NaccasReason {
  id: string;
  label: string;
}

interface NaccasReasonApiResponse {
  text: string;
  value: string;
}

interface NaccasDropReasonMappingProps {
  campusId: string;
  initialMappings: DropMapping[];
  onMappingsChange: (mappings: DropMapping[]) => void;
}

const NaccasDropReasonMapping: React.FC<NaccasDropReasonMappingProps> = ({
  campusId,
  initialMappings,
  onMappingsChange,
}) => {
  const classes = useStyles();
  const [mappings, setMappings] = useState<DropMapping[]>([]);
  const [naccasReasons, setNaccasReasons] = useState<NaccasReason[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNaccasReasons = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getNaccasDropReasons();

        const formattedReasons: NaccasReason[] = data.map(
          (item: NaccasReasonApiResponse) => ({
            id: item.value.toLowerCase(),
            label: item.text,
          })
        );
        setNaccasReasons(formattedReasons);
      } catch (err) {
        console.error("Failed to fetch NACCAS drop reasons:", err);
        setError("Failed to load NACCAS drop reasons. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchNaccasReasons();
  }, []);

  useEffect(() => {
    if (!initialMappings) return;

    if (
      !isInitialized ||
      JSON.stringify(initialMappings) !== JSON.stringify(mappings)
    ) {
      setMappings([...initialMappings]);

      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [initialMappings, isInitialized]);

  useEffect(() => {
    if (isInitialized && mappings.length > 0) {
      const timeoutId = setTimeout(() => {
        onMappingsChange(mappings);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [mappings, onMappingsChange, isInitialized]);

  const updateAdvantageReasons = useCallback(
    (naccasReasonId: string, advantageReasons: string[]) => {
      setMappings((prevMappings) => {
        const existingIndex = prevMappings.findIndex(
          (m) => m.naccasReasonId === naccasReasonId
        );

        const newMappings = [...prevMappings];

        if (existingIndex >= 0) {
          newMappings[existingIndex] = {
            ...newMappings[existingIndex],
            advantageReasons: advantageReasons,
          };
        } else {
          newMappings.push({ naccasReasonId, advantageReasons });
        }

        return newMappings;
      });
    },
    []
  );

  const handleFilterChange = useCallback(
    (naccasReasonId: string, values: any) => {
      let selectedIds: string[] = [];

      if (values) {
        if (Array.isArray(values)) {
          selectedIds = values.map((v: any) =>
            typeof v === "object" ? v.value : v
          );
        } else if (typeof values === "object") {
          selectedIds = [values.value];
        } else {
          selectedIds = [values];
        }
      }
      updateAdvantageReasons(naccasReasonId, selectedIds);
    },
    [updateAdvantageReasons]
  );

  const getNaccasReasonLabel = (id: string) => {
    if (!id) return "";
    const normalizedId = id.toLowerCase();
    const reason = naccasReasons.find((r) => r.id === normalizedId);
    return reason ? reason.label : id;
  };

  return (
    <div className={classes.section}>
      <div className={classes.header}>
        <Typography variant="h6">NACCAS Drop Reason Mappings</Typography>
      </div>

      {isLoading && naccasReasons.length === 0 ? (
        <Typography>Loading NACCAS drop reasons...</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={5}>
                  <Typography variant="subtitle1" gutterBottom>
                    NACCAS Drop Reason
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="subtitle1" gutterBottom>
                    System Drop Reason
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider style={{ margin: "16px 0" }} />

          {mappings.map((mapping, index) => {
            const naccasReason = naccasReasons.find(
              (r) => r.id === mapping.naccasReasonId.toLowerCase()
            );
            const naccasLabel = naccasReason?.label || mapping.naccasReasonId;

            const handleChange = (values: any) => {
              handleFilterChange(mapping.naccasReasonId, values);
            };

            return (
              <div
                key={`${mapping.naccasReasonId}-${index}`}
                className={classes.reasonSection}
              >
                <Grid container spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="h6" gutterBottom>
                      {naccasLabel}
                    </Typography>

                    <FormControl
                      className={classes.formControl}
                      style={{ marginTop: 8 }}
                    ></FormControl>
                  </Grid>

                  <Grid item xs={7}>
                    <DropReasonAutoComplete
                      valueFilter={
                        mapping.advantageReasons &&
                        mapping.advantageReasons.length > 0
                          ? {
                              key: "value",
                              values: mapping.advantageReasons,
                            }
                          : undefined
                      }
                      id={`advantage-reasons-${index}`}
                      name={`advantage-reasons-${index}`}
                      multiple={true}
                      filterHandle={handleChange}
                      campusId={campusId}
                      params={{
                        fullWidth: true,
                        onChange: (e: any, values: any) => {
                          handleChange(values);
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default NaccasDropReasonMapping;
